import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { SellableCatalog } from '@/features/pos/types'
import { apiClient } from '@/lib/apiClient'
import { accessToken } from '@/lib/staffSession'

import type {
  FloorOrder,
  MyService,
  Quote,
  SettleResult,
  StationMenuItem,
  StationOrders,
  WaiterBalance,
} from './types'

/**
 * Le service en salle et les postes.
 *
 * Comme toutes les surfaces `/ops`, chaque appel porte le jeton **et**
 * l'en-tête d'édition : un rôle attribué sur une édition n'accorde rien sans
 * lui.
 */
async function authorized(eventId: string | undefined) {
  return { token: await accessToken(), ...(eventId ? { eventId } : {}) }
}

export interface CartLine {
  kind: 'item' | 'pack' | 'drink'
  ref_id: string
  quantity: number
}

export type Station = 'kitchen' | 'bar'

/** Tout ce qui bouge quand une commande change d'état. */
const TOUCHED = [
  ['service-me'],
  ['service-orders'],
  ['station-orders'],
  ['station-queue'],
  ['pos-session'],
  ['pos-movements'],
  ['handover-balances'],
  ['onsite-cash'],
]

function useInvalidateFloor() {
  const queryClient = useQueryClient()
  return () => {
    for (const key of TOUCHED) void queryClient.invalidateQueries({ queryKey: key })
  }
}

// ── L'espace serveur ───────────────────────────────────────────────

export function useMyService(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['service-me', eventId],
    queryFn: async () => apiClient.get<MyService>('/ops/service/me', await authorized(eventId)),
    enabled: Boolean(eventId) && enabled,
    refetchInterval: 15_000,
  })
}

export function useServiceCatalogue(eventId: string | undefined, enabled: boolean) {
  const { i18n } = useTranslation()
  return useQuery({
    queryKey: ['service-catalogue', eventId, i18n.language],
    queryFn: async () =>
      apiClient.get<SellableCatalog>(
        `/ops/service/catalogue?locale=${i18n.language}`,
        await authorized(eventId),
      ),
    enabled: Boolean(eventId) && enabled,
    refetchInterval: 60_000,
  })
}

/**
 * Le total du panier, chiffré par le serveur à chaque changement.
 *
 * La clé porte le panier lui-même : deux paniers identiques ne coûtent qu'un
 * appel, et une réponse arrivée en retard ne peut pas écraser le total d'un
 * panier plus récent.
 */
export function useServiceQuote(eventId: string | undefined, lines: CartLine[]) {
  const { i18n } = useTranslation()
  return useQuery({
    queryKey: ['service-quote', eventId, i18n.language, lines],
    queryFn: async () =>
      apiClient.post<Quote>(
        `/ops/service/quote?locale=${i18n.language}`,
        { lines },
        await authorized(eventId),
      ),
    enabled: Boolean(eventId) && lines.length > 0,
    placeholderData: (previous) => previous,
    retry: false,
  })
}

export function useMyOrders(
  eventId: string | undefined,
  scope: 'active' | 'done',
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['service-orders', eventId, scope],
    queryFn: async () =>
      apiClient.get<FloorOrder[]>(`/ops/service/orders?scope=${scope}`, await authorized(eventId)),
    enabled: Boolean(eventId) && enabled,
    // Une table commande par QR pendant qu'on sert la voisine : la carte doit
    // apparaître sans qu'on touche à l'écran.
    refetchInterval: 10_000,
  })
}

/**
 * Une commande prise par le serveur : à une table, ou sans table.
 *
 * Sans table, le prénom est exigé (le serveur le refuse sinon), le repère est
 * facultatif, et `fulfilment` dit qui remet la commande — le serveur
 * (`deliver`) ou le comptoir (`pickup`).
 */
export type TakeOrderPayload = {
  lines: CartLine[]
  payment_method: 'cash' | 'paypal'
  note: string | null
} & (
  | { table_id: string }
  | {
      table_id: null
      customer_label: string
      spot_note: string | null
      fulfilment: 'deliver' | 'pickup'
    }
)

export function useTakeOrder(eventId: string | undefined) {
  const invalidate = useInvalidateFloor()
  const { i18n } = useTranslation()
  return useMutation({
    mutationFn: async (payload: TakeOrderPayload) =>
      apiClient.post<FloorOrder>(
        `/ops/service/orders?locale=${i18n.language}`,
        payload,
        await authorized(eventId),
      ),
    onSuccess: invalidate,
  })
}

export interface SettlePayload {
  orderId: string
  received_minor: number | null
  method: 'cash' | 'paypal' | null
}

/**
 * Encaisser — par le serveur (`station` nul) ou au poste.
 *
 * Au poste, l'argent entre dans la caisse qu'on y tient : le serveur refuse
 * si l'on n'en tient pas.
 */
export function useSettle(eventId: string | undefined, station: Station | null) {
  const invalidate = useInvalidateFloor()
  return useMutation({
    mutationFn: async ({ orderId, received_minor, method }: SettlePayload) =>
      apiClient.post<SettleResult>(
        station
          ? `/ops/${station}/orders/${orderId}/settle`
          : `/ops/service/orders/${orderId}/settle`,
        {
          ...(received_minor !== null ? { received_minor } : {}),
          ...(method ? { method } : {}),
        },
        await authorized(eventId),
      ),
    onSuccess: invalidate,
  })
}

/** Annuler — jamais supprimer. Le motif est exigé par le serveur. */
export function useCancelOrder(eventId: string | undefined, station: Station | null) {
  const invalidate = useInvalidateFloor()
  return useMutation({
    mutationFn: async ({ orderId, reason }: { orderId: string; reason: string }) =>
      apiClient.post<FloorOrder>(
        station
          ? `/ops/${station}/orders/${orderId}/cancel`
          : `/ops/service/orders/${orderId}/cancel`,
        { reason },
        await authorized(eventId),
      ),
    onSuccess: invalidate,
  })
}

// ── Les postes ─────────────────────────────────────────────────────

export function useStationOrders(
  station: Station,
  eventId: string | undefined,
  scope: 'active' | 'done' | 'cancelled',
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['station-orders', station, eventId, scope],
    queryFn: async () =>
      apiClient.get<StationOrders>(
        `/ops/${station}/orders?scope=${scope}`,
        await authorized(eventId),
      ),
    enabled: Boolean(eventId) && enabled,
    // Une cuisine ne rafraîchit pas sa page : l'écran se tient à jour seul,
    // et le chrono de chaque carte avance avec lui.
    refetchInterval: 10_000,
  })
}

export function useMarkDone(station: Station, eventId: string | undefined) {
  const invalidate = useInvalidateFloor()
  return useMutation({
    mutationFn: async (orderId: string) =>
      apiClient.post<FloorOrder>(
        `/ops/${station}/orders/${orderId}/done`,
        undefined,
        await authorized(eventId),
      ),
    onSuccess: invalidate,
  })
}

// ── La carte du poste : marquer épuisé ─────────────────────────────

export function useStationMenu(station: Station, eventId: string | undefined, enabled: boolean) {
  const { i18n } = useTranslation()
  return useQuery({
    queryKey: ['station-menu', station, eventId, i18n.language],
    queryFn: async () =>
      apiClient.get<StationMenuItem[]>(
        `/ops/${station}/menu?locale=${i18n.language}`,
        await authorized(eventId),
      ),
    enabled: Boolean(eventId) && enabled,
    refetchInterval: 30_000,
  })
}

/**
 * « Épuisé » / « Disponible ». Le produit disparaît aussitôt de la commande à
 * table, de l'écran des serveurs et de la caisse : on rafraîchit ce qui les
 * nourrit sur cet appareil.
 */
export function useSetSoldOut(station: Station, eventId: string | undefined) {
  const queryClient = useQueryClient()
  const { i18n } = useTranslation()
  return useMutation({
    mutationFn: async (payload: { kind: StationMenuItem['kind']; ref_id: string; sold_out: boolean }) =>
      apiClient.post<StationMenuItem>(
        `/ops/${station}/menu/${payload.kind}/${payload.ref_id}/sold-out?locale=${i18n.language}`,
        { sold_out: payload.sold_out },
        await authorized(eventId),
      ),
    onSuccess: () => {
      for (const key of [['station-menu'], ['pos-catalogue'], ['service-catalogue']]) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })
}

// ── Les remises à la caisse ────────────────────────────────────────

export function useHandoverBalances(eventId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['handover-balances', eventId],
    queryFn: async () =>
      apiClient.get<WaiterBalance[]>('/ops/pos/handovers/balances', await authorized(eventId)),
    enabled: Boolean(eventId) && enabled,
    refetchInterval: 30_000,
  })
}

export function useReceiveHandover(eventId: string | undefined) {
  const invalidate = useInvalidateFloor()
  return useMutation({
    mutationFn: async (payload: { waiter_id: string; amount_minor: number }) =>
      apiClient.post('/ops/pos/session/handovers', payload, await authorized(eventId)),
    onSuccess: invalidate,
  })
}
